import { useCallback } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { ChevronBackward, Edit } from '@nine-thirty-five/material-symbols-react/rounded'
import { useGetCompletions, useGetHabit } from '@zendia/frontend-shared'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { HabitView } from '../../../../components/Habits/HabitView'
import { axiosRequest } from '../../../../utilities/axiosClient'
import { useToday } from '../../../../hooks/useToday'

export const Route = createFileRoute('/_authenticated/habits/$habitId/')({
  component: RouteComponent,
  validateSearch: (search): { goalId?: string; milestoneId?: string } => ({
    // Set when the habit was opened from a goal's Your Plan, so leaving returns there, on the
    // same milestone
    goalId: search.goalId as string | undefined,
    milestoneId: search.milestoneId as string | undefined,
  }),
})

function RouteComponent() {
  const router = useRouter()
  const { habitId } = Route.useParams()
  const { goalId, milestoneId } = Route.useSearch()
  const today = useToday()

  const { data: habitResponse, isLoading: isLoadingHabit } = useGetHabit(axiosRequest, habitId)
  const habit = habitResponse?.data
  // A habit inside a routine is performed on the routine's schedule
  const { data: routineResponse, isLoading: isLoadingRoutine } = useGetHabit(
    axiosRequest,
    habit?.parentId ?? '',
    !!habit?.parentId,
  )
  // With a habit id the API returns every one of its completions, which the streaks need
  const { data: completionsResponse, isLoading: isLoadingCompletions } = useGetCompletions(
    axiosRequest,
    { habitId },
  )

  const onBack = useCallback(() => {
    if (goalId) {
      router.navigate({
        to: '/goals/$goalId',
        params: { goalId },
        search: { tab: 'plan', planMilestone: milestoneId },
        replace: true,
      })
    } else {
      router.navigate({ to: '/dashboard', replace: true })
    }
  }, [router, goalId, milestoneId])

  const onEdit = useCallback(() => {
    router.navigate({
      to: '/habits/$habitId/edit',
      params: { habitId },
      search: { goalId, milestoneId, fromView: true },
    })
  }, [router, habitId, goalId, milestoneId])

  const backButton = (
    <ActionButton onClick={onBack} label="Back" icon={<ChevronBackward className="size-6" />} />
  )

  const isLoading =
    isLoadingHabit || (!!habit?.parentId && isLoadingRoutine) || isLoadingCompletions
  if (isLoading || !habit) {
    return (
      <FullView leftAction={backButton} centerContent={<div>Habit</div>}>
        <FullViewContent>
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500">{isLoading ? 'Loading...' : 'Habit not found'}</p>
          </div>
        </FullViewContent>
      </FullView>
    )
  }

  return (
    <FullView
      leftAction={backButton}
      centerContent={<div>{habit.isRoutine ? 'Routine' : 'Habit'}</div>}
      rightAction={
        <ActionButton
          onClick={onEdit}
          label={habit.isRoutine ? 'Edit routine' : 'Edit habit'}
          icon={<Edit className="size-5" />}
        />
      }
    >
      <FullViewContent>
        <HabitView
          habit={habit}
          routine={routineResponse?.data}
          completions={completionsResponse?.data?.completions ?? []}
          today={today}
        />
      </FullViewContent>
    </FullView>
  )
}
